#version 300 es
precision highp float;
precision highp int;

// Silmarillion resolve — domain-warped storm noise that hue-shifts from
// green (the "wrong" color the system kept choosing) to deep blue
// (the resolution) as u_resolve animates 0 → 1.

#include "_lib.glsl"

in vec2 v_uv;
out vec4 fragColor;

uniform float u_time;
uniform vec2  u_resolution;
uniform float u_resolve;     // 0 = green storm, 1 = blue calm
uniform float u_intensity;   // overall opacity gate, 0..1
uniform float u_dark;

void main() {
  vec2 uv = v_uv;
  float aspect = u_resolution.x / max(u_resolution.y, 1.0);
  vec2 p = vec2((uv.x - 0.5) * aspect, uv.y - 0.5);

  // Storm structure — domain-warped FBM, pulled slowly by time.
  vec2 q = p * 1.8 + vec2(u_time * 0.06, -u_time * 0.05);
  float storm = fbmWarp(q, u_time * 0.55);
  float churn = fbm5(q * 2.4 + vec2(0.0, u_time * 0.12));

  // Muted palettes — atmospheric, not aggressive.
  // Green storm: forest → moss → muted emerald → soft sage.
  vec3 g0 = vec3(0.02, 0.06, 0.03);
  vec3 g1 = vec3(0.05, 0.20, 0.10);
  vec3 g2 = vec3(0.14, 0.40, 0.22);
  vec3 g3 = vec3(0.32, 0.55, 0.36);
  vec3 g4 = vec3(0.60, 0.75, 0.62);

  // Silmarillion blue: midnight → ink → muted lapis → soft cornflower → mist.
  vec3 b0 = vec3(0.01, 0.04, 0.12);
  vec3 b1 = vec3(0.05, 0.14, 0.32);
  vec3 b2 = vec3(0.14, 0.30, 0.58);
  vec3 b3 = vec3(0.36, 0.52, 0.78);
  vec3 b4 = vec3(0.65, 0.78, 0.92);

  float darkScale = mix(1.0, 0.55, u_dark);

  vec3 greenCol = palette5(storm, g0, g1, g2, g3, g4) * darkScale;
  vec3 blueCol  = palette5(storm, b0, b1, b2, b3, b4) * darkScale;

  // Lerp green → blue with a smoothstep — eases the transition.
  float t = smoothstep(0.0, 1.0, u_resolve);
  vec3 col = mix(greenCol, blueCol, t);

  // Soft churn highlights, but quieter — no bright flashes.
  float crest = smoothstep(0.7, 0.95, churn);
  vec3 crestCol = mix(vec3(0.45, 0.65, 0.50), vec3(0.55, 0.70, 0.85), t) * darkScale;
  col += crest * 0.18 * crestCol;

  // Heavier vignette — text in the middle of the section stays readable.
  // Edge intensity drops to ~35% of center so the wash settles around text.
  float vig = vignette(uv, 0.78, 0.55);
  col *= mix(0.42, 1.0, vig);

  // Subtle grain.
  float grain = (hash(gl_FragCoord.xy + u_time * 23.0) - 0.5) * 0.014;
  col += grain;

  fragColor = vec4(col, clamp(u_intensity, 0.0, 1.0));
}
